let isInitialized = false;

export const initMapKit = (token) => {
  if (!globalThis.mapkit || !token) {
    return false;
  }
  if (!isInitialized) {
    try {
      globalThis.mapkit.init({
        authorizationCallback: (done) => {
          done(token);
        },
      });
      isInitialized = true;
    } catch (e) {
      if (e?.message?.toLowerCase().includes("already initialized")) {
        isInitialized = true;
      } else {
        console.error("MapKit init error:", e);
        return false;
      }
    }
  }
  return isInitialized;
};

export const initMapKitAsync = (token, timeoutMs = 4000) => {
  return new Promise((resolve) => {
    if (initMapKit(token)) {
      resolve(true);
      return;
    }
    if (!token) {
      resolve(false);
      return;
    }

    const startTime = Date.now();
    const interval = setInterval(() => {
      if (initMapKit(token)) {
        clearInterval(interval);
        resolve(true);
      } else if (Date.now() - startTime >= timeoutMs) {
        clearInterval(interval);
        resolve(false);
      }
    }, 150);
  });
};

export const getIsInitialized = () => isInitialized;
export const setIsInitializedForTest = (val) => {
  isInitialized = val;
};

export const searchPlaces = (query, centerCoords = null, spanDelta = 0.5) => {
  return new Promise((resolve) => {
    if (!globalThis.mapkit || !isInitialized || !query) {
      resolve([]);
      return;
    }
    try {
      const center = centerCoords
        ? new globalThis.mapkit.Coordinate(
            centerCoords.latitude,
            centerCoords.longitude,
          )
        : new globalThis.mapkit.Coordinate(37.7749, -122.4194);
      const span = new globalThis.mapkit.CoordinateSpan(spanDelta, spanDelta);
      const region = new globalThis.mapkit.CoordinateRegion(center, span);
      const search = new globalThis.mapkit.Search({ region });

      search.search(query, (error, data) => {
        if (error) {
          console.error("MapKit search error:", error);
          resolve([]);
          return;
        }
        resolve(data?.places || []);
      });
    } catch (err) {
      console.error("MapKit search exception:", err);
      resolve([]);
    }
  });
};

export const reverseGeocode = (latitude, longitude) => {
  return new Promise((resolve) => {
    if (!globalThis.mapkit || !isInitialized) {
      resolve(null);
      return;
    }
    try {
      const geocoder = new globalThis.mapkit.Geocoder({ language: "en-US" });
      const coord = new globalThis.mapkit.Coordinate(latitude, longitude);

      let callbackHandled = false;
      const handleData = (data) => {
        if (callbackHandled) return;
        callbackHandled = true;
        if (!data) return resolve(null);
        if (data.results && data.results.length > 0)
          return resolve(data.results);
        if (data.places && data.places.length > 0) return resolve(data.places);
        resolve(null);
      };

      const result = geocoder.reverseLookup(coord, (err, data) => {
        if (err) {
          console.error("MapKit reverse lookup error:", err);
          if (!callbackHandled) resolve(null);
          return;
        }
        handleData(data);
      });

      if (result && typeof result.then === "function") {
        result.then(handleData).catch((err) => {
          console.error("MapKit reverse lookup promise error:", err);
          if (!callbackHandled) resolve(null);
        });
      }
    } catch (err) {
      console.error("MapKit reverse lookup exception:", err);
      resolve(null);
    }
  });
};

export const getUserLocation = () => {
  return new Promise((resolve, reject) => {
    if (!navigator.geolocation) {
      reject(new Error("Geolocation is not supported by this browser."));
      return;
    }
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const { latitude, longitude } = position.coords;
        const results = await reverseGeocode(latitude, longitude);
        resolve({
          latitude,
          longitude,
          results: results || [],
        });
      },
      (err) => {
        if (err instanceof Error) {
          reject(err);
          return;
        }
        let message = "Geolocation error";
        if (err && typeof err.message === "string") {
          message = err.message;
        } else if (typeof err === "string") {
          message = err;
        }
        reject(new Error(message));
      },
      { enableHighAccuracy: false, timeout: 10000 },
    );
  });
};
